import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { FilePdf, Image as ImageIcon, ArrowSquareOut, Info } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Sprint 5.3 — Archivio Brand & Documenti aziendali
 *
 * Raccoglie gli asset di sistema che tutti gli operatori condividono:
 * logo email, Condizioni Generali, Informativa Privacy.
 * Upload interattivo è P1 backlog — per ora read-only + link di download diretto.
 */
export default function BrandDocumentsPage() {
  const [assets, setAssets] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAssets = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/brand/manifest`, { withCredentials: true });
      setAssets(data);
    } catch {
      setAssets({ logos: [], documents: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAssets(); }, [fetchAssets]);

  if (loading) return <div className="p-8 text-slate-500">Caricamento archivio...</div>;

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="brand-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 mb-2">Brand & Documenti</h1>
        <p className="text-slate-500">
          Asset aziendali usati in tutte le email e PDF generati dal software. Cambiali qui per aggiornarli ovunque.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex gap-3">
        <Info size={22} weight="fill" className="text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900">
          <strong>Come funziona il pescaggio automatico:</strong> ogni azienda che usa il software ha i propri asset brand qui.
          Quando invii un pre-contratto, il logo e la firma nella email sono letti da questi file + dai campi "Contatti footer" in{' '}
          <a href="/settings" className="underline font-medium">Impostazioni</a>. Nessun hard-coding: cambi qui, cambia ovunque.
        </div>
      </div>

      {/* Logo */}
      <div className="bg-white border border-slate-200 rounded-sm p-6 mb-6">
        <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-3 flex items-center gap-2">
          <ImageIcon size={22} weight="fill" /> Logo email
        </h2>
        <div className="grid grid-cols-2 gap-4">
          <LogoPreview label="Logo nero (email chiare)" src={`${API}/api/brand/logo-black.png`} />
          <LogoPreview label="Logo bianco (email scure)" src={`${API}/api/brand/logo-white.png`} />
        </div>
        <p className="text-xs text-slate-500 mt-3">
          Formato consigliato: PNG trasparente, altezza ~200px. Upload manuale disponibile nella prossima release.
        </p>
      </div>

      {/* Documenti legali */}
      <div className="bg-white border border-slate-200 rounded-sm p-6">
        <h2 className="text-xl font-bold tracking-tight text-slate-900 mb-3 flex items-center gap-2">
          <FilePdf size={22} weight="fill" /> Documenti legali
        </h2>
        <p className="text-sm text-slate-500 mb-4">
          Allegati PDF che vengono inseriti (opzionalmente) in ogni invio pre-contratto dal composer.
        </p>
        <div className="space-y-2">
          <DocumentRow label="Condizioni Generali di Fornitura"
                       filename="Condizioni_Generali_Orange_Energy.pdf" />
          <DocumentRow label="Informativa Privacy GDPR"
                       filename="Informativa_Privacy_Orange_Energy.pdf" />
        </div>
      </div>
    </div>
  );
}

function LogoPreview({ label, src }) {
  const [ok, setOk] = useState(true);
  return (
    <div className="border rounded-lg p-4 bg-slate-50">
      <div className="text-xs font-medium text-slate-600 mb-2">{label}</div>
      <div className={`flex items-center justify-center h-20 rounded ${label.includes('bianco') ? 'bg-slate-900' : 'bg-white border'}`}>
        {ok
          ? <img src={src} alt={label} className="max-h-16 w-auto" onError={() => setOk(false)} />
          : <span className="text-xs text-slate-400">Non disponibile</span>}
      </div>
    </div>
  );
}

function DocumentRow({ label, filename }) {
  // Link to the PDF via pre-contracts download endpoint (auth-protected)
  const link = `${API}/api/pre-contracts/documents/${encodeURIComponent(filename)}/download`;
  return (
    <div className="flex items-center justify-between border rounded p-3 hover:bg-slate-50">
      <div className="flex items-center gap-3">
        <FilePdf size={22} weight="fill" className="text-red-500" />
        <div>
          <div className="font-medium text-sm text-slate-900">{label}</div>
          <div className="text-xs font-mono text-slate-400">{filename}</div>
        </div>
      </div>
      <a href={link} target="_blank" rel="noreferrer"
         className="flex items-center gap-1 text-sm text-orange-600 hover:text-orange-700"
         data-testid={`brand-doc-${filename}`}>
        Apri <ArrowSquareOut size={14} />
      </a>
    </div>
  );
}
